import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export const buyStatePass = async (setBusy) => {
    setBusy?.(true);
    try {
        const r = await api.post("/payments/checkout", { lookup_key: "state_pass", origin_url: window.location.origin });
        window.location.href = r.data.checkout_url;
    } catch (e) {
        toast.error(e.response?.data?.detail || "Could not start checkout");
        setBusy?.(false);
    }
};

/**
 * Banner pushing the one-time $20 State Pass. Renders:
 * - logged out → sign-in/register CTA
 * - logged in, no pass → buy CTA (Stripe checkout)
 * - pass active → slim confirmation chip
 */
export const StatePassBanner = () => {
    const { user, ready } = useAuth();
    const [busy, setBusy] = useState(false);
    if (!ready) return null;

    if (user?.has_pass) {
        return (
            <div className="border border-volt/40 clip-frame-sm bg-volt/[0.06] px-6 py-4 flex items-center justify-between" data-testid="state-pass-active">
                <div className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-volt live-dot" />
                    <span className="font-mono text-[11px] tracking-[0.24em] uppercase text-volt">State Pass active</span>
                </div>
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/50 hidden md:block">
                    Every {user.state || "home-state"} bracket + free cash cups unlocked
                </span>
            </div>
        );
    }

    return (
        <div className="relative border border-volt/30 clip-frame bg-gradient-to-r from-[#150A22] to-[#0A0A0A] p-6 md:p-8 overflow-hidden" data-testid="state-pass-banner">
            <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-gradient-to-l from-volt/10 to-transparent pointer-events-none" />
            <div className="relative grid md:grid-cols-12 gap-6 items-center">
                <div className="md:col-span-8">
                    <div className="overline text-volt mb-2">[ Season 1 · State Pass ]</div>
                    <h3 className="font-display font-black tracking-tighter text-2xl md:text-4xl leading-[0.95]">
                        One pass. Every bracket <span className="text-outline-strong">in your state.</span>
                    </h3>
                    <p className="mt-3 text-secondary text-sm md:text-base max-w-xl leading-relaxed">
                        A one-time <span className="text-white font-bold">$20 State Pass</span> unlocks all of your home state's Warzone and MW4 Multiplayer qualifiers — plus every free cash cup, every week. State-locked. No subscriptions.
                    </p>
                    <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="state-pass-coming-soon">
                        <span className="font-mono text-[9px] tracking-[0.26em] uppercase text-white/40 mr-1">Coming soon:</span>
                        {["Apex Legends", "R6 Siege", "Madden", "NBA 2K", "GTA 6"].map((g) => (
                            <span key={g} className="border border-white/15 bg-white/[0.04] px-2.5 py-1 font-mono text-[9px] tracking-[0.2em] uppercase text-white/55">
                                {g}
                            </span>
                        ))}
                    </div>
                </div>
                <div className="md:col-span-4 md:text-right">
                    <div className="font-display font-black text-4xl md:text-5xl text-volt mb-3">$20</div>
                    {user ? (
                        <button
                            onClick={() => buyStatePass(setBusy)}
                            disabled={busy}
                            className="btn-primary w-full md:w-auto text-center disabled:opacity-40"
                            data-testid="buy-state-pass-btn"
                        >
                            {busy ? "Opening checkout…" : "Get the State Pass →"}
                        </button>
                    ) : (
                        <Link to="/register" className="btn-primary inline-block w-full md:w-auto text-center" data-testid="state-pass-register-cta">
                            Register to get yours →
                        </Link>
                    )}
                    <div className="mt-2 font-mono text-[9px] tracking-[0.22em] uppercase text-white/40">One-time · Secure checkout by Stripe</div>
                </div>
            </div>
        </div>
    );
};
